import React, { useState, useEffect } from 'react';
import { Box, FormControl, FormHelperText, InputLabel, Tooltip } from '@mui/material';
import ReactQuill from 'react-quill';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import 'react-quill/dist/quill.snow.css';

const TextAreaWithToolbar = ({
    value,
    onChange,
    label,
    errorMessage,
    isEditable = true,
    toolbarId,
    minHeight,
    placeholder = '',
    showToolbar = true
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpand = () => setIsExpanded(prev => !prev);
    const computedHeight = minHeight ?? (isEditable ? 120 : 80);

    useEffect(() => {
        if (isEditable) {
            const toolbar = document.getElementById(toolbarId);
            if (toolbar) {
                const tooltips = {
                    '.ql-bold': 'Bold',
                    '.ql-italic': 'Italic',
                    '.ql-underline': 'Underline',
                    '.ql-strike': 'Strike-through',
                    '.ql-list[value="ordered"]': 'Numbered List',
                    '.ql-list[value="bullet"]': 'Bullet List',
                    '.ql-indent[value="-1"]': 'Outdent',
                    '.ql-indent[value="+1"]': 'Indent',
                    '.ql-color': 'Text Color',
                    '.ql-background': 'Background Color',
                    '.ql-align': 'Align',
                    '.ql-link': 'Insert Link',
                    '.ql-clean': 'Clear Formatting',
                    '.ql-header': 'Header',
                };

                Object.entries(tooltips).forEach(([selector, title]) => {
                    const button = toolbar.querySelector(selector);
                    if (button) button.setAttribute('title', title);
                });
            }
        }
    }, [toolbarId, isEditable]);

    return (
        <FormControl fullWidth margin="normal" error={!!errorMessage}>
            {label && <InputLabel shrink>{label}</InputLabel>}

            <Box
                sx={{
                    border: errorMessage ? '1px solid red' : '1px solid #E0E0E0',
                    borderRadius: 1,
                    fontSize: 13,
                    backgroundColor: '#F3F3F5',
                    display: 'flex',
                    flexDirection: 'column',
                    height: isExpanded ? 300 : computedHeight,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        '& .ql-container': {
                            border: 'none !important',
                            fontSize: '13px',
                            flex: 1,
                            backgroundColor: '#F3F3F5',
                        },
                        '& .ql-editor': {
                            minHeight: 60,
                            maxHeight: isExpanded ? 220 : computedHeight - 40,
                            overflowY: 'auto',
                            padding: '8px',
                            backgroundColor: '#F3F3F5'
                        },
                    }}
                >
                    <ReactQuill
                        theme="snow"
                        value={value ?? ''}
                        onChange={onChange}
                        readOnly={!isEditable}
                        placeholder={placeholder}
                        modules={showToolbar ? { toolbar: `#${toolbarId}` } : { toolbar: false }}
                        style={{
                            flex: 1,
                            border: 'none',
                            fontSize: 13,
                            backgroundColor: '#FAFAFA',
                        }}
                    />

                </Box>

                {isEditable && showToolbar &&(
                    <Box
                        id={toolbarId}
                        className="ql-toolbar"
                        sx={{
                            borderTop: '1px solid #E0E0E0',
                            px: 1,
                            py: 1,
                            backgroundColor: '#FFFF',
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            overflowX: 'auto',
                            margin: 1,
                            borderRadius: 1,  
                        }}
                    >
                        <select className="ql-header" style={{ border: 'none', outline: 'none' }}>
                            <option value="">Normal</option>
                            <option value="1">H1</option>
                            <option value="2">H2</option>
                        </select>

                        <Box sx={{ mx: 1, borderLeft: '1px solid #ccc', height: 20 }} />

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <button className="ql-bold" />
                            <button className="ql-italic" />
                            <button className="ql-underline" />
                            <button className="ql-strike" />
                        </Box>

                        <Box sx={{ mx: 1, borderLeft: '1px solid #ccc', height: 20 }} />

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <button className="ql-align" value="" />
                            <button className="ql-align" value="center" />
                            <button className="ql-align" value="right" />
                        </Box>

                        <Box sx={{ mx: 1, borderLeft: '1px solid #ccc', height: 20 }} />

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <button className="ql-list" value="bullet" />
                            <button className="ql-list" value="ordered" />
                        </Box>

                        <Box sx={{ mx: 1, borderLeft: '1px solid #ccc', height: 20 }} />

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <select className="ql-color" />
                            <button className="ql-link" />
                            <button className="ql-clean" />
                        </Box>

                        <Box sx={{ ml: 'auto' }}>
                            <Tooltip title={isExpanded ? 'Minimize' : 'Maximize'} arrow>
                                <OpenInFullIcon
                                    onClick={toggleExpand}
                                    sx={{
                                        transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease-in-out',
                                        fontSize: 18,
                                        color: '#555',
                                        cursor: 'pointer',
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>

            {errorMessage && (
                <FormHelperText sx={{ color: 'red' }}>{errorMessage}</FormHelperText>
            )}
        </FormControl>
    );
};

export default TextAreaWithToolbar;
